import dayjs from "dayjs";
import React from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useHistory } from "react-router-dom";
import { Permission } from "types";
import shallow from "zustand/shallow";
import { useSeller } from "../../../hooks/useAccount";
import { IsGranted } from "../../Common/IsGranted";
import { AvailabilityScopeButtons } from "./AvailabilityScopeButtons";
import { DashboardDateFilter } from "./DashboardDateFilter";
import { DashboardDisplayOptions } from "./DashboardDisplayOptions";
import { DashboardGroupByFilter } from "./DashboardGroupByFilter";
import { ListFilter, useProductFilterWithColor } from "./ListFilter";
import { useDashboardFilterStore } from "@/stores/dashboardFilterStore";
import { DashboardMode, DashboardScope } from "@/types/Dashboard";

// Proof of concept shortcuts. This is a combination of chars, not a sequence like gmail
const shortcuts: any = {
    "g+r": DashboardScope.RESERVED,
    "g+w": DashboardScope.WAITLIST,
    "g+a": DashboardScope.AVAILABLE,
    "g+l": DashboardScope.ALL,
};

export const DashboardFilterList = () => {
    const seller = useSeller();
    const history = useHistory();
    const [date, setDate] = useDashboardFilterStore((state) => [state.date, state.setDate], shallow);
    const [filters, setFilters] = useDashboardFilterStore((state) => [state.filters, state.setFilters], shallow);
    const productFields = useProductFilterWithColor();

    useHotkeys(
        Object.keys(shortcuts).reverse().join(","),
        (e, key) => {
            const scope: DashboardScope = shortcuts[key.shortcut];
            setFilters({ scope });
        },
        [filters.scope],
    );

    const handleDateChange = (date: Date) => {
        history.push("/dashboard");
        setDate(dayjs(date));
    };

    const isTableView = filters.mode === DashboardMode.TABLE;

    if (filters.mode === DashboardMode.MERCHANDISE) {
        return null;
    }

    return (
        <div className="w-full divide-y divide-gray-lighter print:hidden">
            <div className="flex w-full py-2 px-6 xl:px-10">
                <div className="space-x-2">
                    <DashboardDateFilter value={date.toDate()} onChange={handleDateChange} />

                    <IsGranted permissions={Permission.DASHBOARD_AVAILABILITY_VIEW}>
                        <AvailabilityScopeButtons />
                    </IsGranted>

                    {/* Removing includeDeletedExperiences flag as per X2-9556, in future it will be needed once we have a proper solution */}
                    <ListFilter
                        label="Product"
                        title="Filter Products"
                        localize={false}
                        fetch={() => ({ data: productFields })}
                        value={filters.products}
                        onChange={(products) => setFilters({ products })}
                    />

                    <IsGranted permissions={Permission.VIEW_RESOURCES_EQUIPMENT}>
                        <ListFilter
                            show={{ unassignedCheckbox: filters.mode !== "equipment" }}
                            label="Equipment"
                            title="Filter Equipment"
                            localize={false}
                            collection={["resources"]}
                            value={filters.resource}
                            onChange={(resource, showUnassigned) => {
                                setFilters({ resource: { ...resource, showUnassigned } });
                            }}
                        />
                    </IsGranted>

                    <IsGranted permissions={Permission.VIEW_RESOURCES_GUIDE}>
                        <ListFilter
                            show={{ unassignedCheckbox: true }}
                            label="Guide"
                            title="Filter Guides"
                            localize={false}
                            collection={["sellers", seller.id, "guides"]}
                            value={filters.guide}
                            onChange={(guide, showUnassigned) => {
                                setFilters({ guide: { ...guide, showUnassigned } });
                            }}
                        />
                    </IsGranted>

                    {isTableView && <DashboardGroupByFilter />}
                </div>
                {isTableView && <DashboardDisplayOptions />}
            </div>
        </div>
    );
};
